import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { BRAND, errorMessage, esc, formatDateTime, handleError, useBodyScrollLock } from "./shared";

// Import Excel Student Package: pilih workbook → preview (tidak mengubah data)
// → konfirmasi → hasil. Semua perhitungan dilakukan server; halaman ini hanya
// menampilkan hasilnya dan mengirim id run + hash rencana yang dilihat admin.
const MAX_BYTES = 5 * 1024 * 1024;

const ROLE_LABEL = {
  student_id: "Student ID (matching key)",
  item: "Package item",
  info: "Information — not imported",
  note: "Notes — not imported",
  unlabeled: "No header — not imported",
  unmapped: "Cannot be mapped",
};

const STATUS_CLASS = {
  previewed: "adm-status--info",
  running: "adm-status--warning",
  completed: "adm-status--success",
  failed: "adm-status--danger",
  superseded: "",
  expired: "",
};

const Section = ({ title, children, open = false, count }) => (
  <details className="spk-imp-section" open={open}>
    <summary>
      {title}
      {count !== undefined && <span className="adm-tab-count">{count}</span>}
    </summary>
    <div className="spk-imp-body">{children}</div>
  </details>
);

const Stat = ({ label, value, tone }) => (
  <div className={`spk-imp-stat${tone ? ` is-${tone}` : ""}`}>
    <span>{label}</span>
    <b>{value}</b>
  </div>
);

const ImportModal = ({ onClose, onImported }) => {
  useBodyScrollLock();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [runs, setRuns] = useState(null);

  const loadRuns = useCallback(async () => {
    try {
      const res = await axios.get("/student-package/import/runs");
      setRuns(res.data.runs);
    } catch (err) {
      if (err?.response?.status === 401) handleError(err, navigate);
      setRuns([]);
    }
  }, [navigate]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy && !Swal.isVisible()) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const choose = (e) => {
    const f = e.target.files && e.target.files[0];
    setPreview(null);
    setResult(null);
    if (!f) return setFile(null);
    if (!/\.xlsx$/i.test(f.name)) {
      e.target.value = "";
      setFile(null);
      Swal.fire({ text: "Choose an .xlsx workbook.", icon: "warning", confirmButtonColor: BRAND });
      return;
    }
    if (f.size > MAX_BYTES) {
      e.target.value = "";
      setFile(null);
      Swal.fire({ text: "The workbook is larger than 5 MB.", icon: "warning", confirmButtonColor: BRAND });
      return;
    }
    setFile(f);
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true);
    setPreview(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append("workbook", file);
      const res = await axios.post("/student-package/import/preview", form);
      setPreview(res.data);
      loadRuns();
    } catch (err) {
      handleError(err, navigate, "Unable to read the workbook.");
    } finally {
      setBusy(false);
    }
  };

  const execute = async () => {
    const r = preview.reconciliation;
    const confirm = await Swal.fire({
      title: "Import into Student Package?",
      html: `<p style="margin:0 0 10px">This changes the package records of <b>${preview.students.matched}</b> matched student(s):</p>
        <ul style="text-align:left;margin:0 auto;display:inline-block">
          <li><b>${r.create}</b> new received record(s)</li>
          <li><b>${r.update}</b> pending → received</li>
          <li><b>${r.remove}</b> record(s) removed (history kept)</li>
          ${r.itemsToCreate.length ? `<li>New item(s): <b>${r.itemsToCreate.map((i) => esc(i.itemName)).join(", ")}</b></li>` : ""}
        </ul>`,
      icon: "warning",
      input: "checkbox",
      inputValue: 0,
      inputPlaceholder: "I have reviewed the preview",
      inputValidator: (checked) => (checked ? null : "Please confirm that you reviewed the preview."),
      showCancelButton: true,
      confirmButtonText: "Run import",
      cancelButtonText: "Cancel",
      confirmButtonColor: BRAND,
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;
    setBusy(true);
    try {
      const res = await axios.post(`/student-package/import/${preview.runId}/execute`, { planHash: preview.planHash });
      setResult(res.data);
      setPreview(null);
      onImported && onImported();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "STALE_PREVIEW" || code === "BLOCKED") {
        Swal.fire({ title: "Preview is out of date", text: errorMessage(err), icon: "info", confirmButtonColor: BRAND });
        setPreview(null);
      } else {
        handleError(err, navigate, "The import failed and was rolled back.");
      }
    } finally {
      setBusy(false);
      loadRuns();
    }
  };

  const downloadPlan = async () => {
    try {
      const res = await axios.get(`/student-package/import/${preview.runId}/plan.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `student-package-import-plan-${preview.runId.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      handleError(err, navigate, "Unable to download the plan.");
    }
  };

  const renderPreview = () => {
    const p = preview;
    const r = p.reconciliation;
    return (
      <>
        {p.blocked ? (
          <div className="spk-callout is-error" role="alert">
            <i className="bi bi-x-octagon" aria-hidden="true"></i>
            <div>
              <p className="spk-callout-title">Import blocked — {p.errors.length} problem(s) must be fixed first</p>
              <p className="spk-callout-text" style={{ margin: 0 }}>
                Fix them in the workbook (or the student data), then preview again. Nothing has been changed.
              </p>
            </div>
          </div>
        ) : p.noChanges ? (
          <div className="spk-callout is-info" role="status">
            <i className="bi bi-check2-circle" aria-hidden="true"></i>
            <p className="spk-callout-text" style={{ margin: 0 }}>
              Student Package already matches this workbook — there is nothing to import.
            </p>
          </div>
        ) : (
          <div className="spk-callout is-warning" role="status">
            <i className="bi bi-eye" aria-hidden="true"></i>
            <p className="spk-callout-text" style={{ margin: 0 }}>
              Preview only — nothing has been changed yet. Review everything below, then confirm the import.
            </p>
          </div>
        )}

        {p.errors.length > 0 && (
          <Section title="Problems blocking the import" count={p.errors.length} open>
            <ul className="spk-imp-list is-error">
              {p.errors.map((e, i) => (
                <li key={i}>{e.message}</li>
              ))}
            </ul>
          </Section>
        )}

        <div className="spk-imp-stats">
          <Stat label="New received" value={r.create} />
          <Stat label="Pending → received" value={r.update} />
          <Stat label="Already received (kept)" value={r.preserve} />
          <Stat label="Removed" value={r.remove} tone={r.remove ? "danger" : ""} />
          <Stat label="History entries" value={r.historyEntries} />
          <Stat label="Batch configs added" value={r.batchConfigsToAdd} />
        </div>

        <Section title="Workbook" open>
          <p className="spk-imp-meta">
            <b>{p.workbook.fileName}</b> · fingerprint {p.workbook.fileHash.slice(0, 12)} · {p.workbook.sheetsInWorkbook.length} sheets in the file, only{" "}
            {p.workbook.importedSheets.map((s) => `“${s}”`).join(" and ")} are imported.
          </p>
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--compact">
              <thead>
                <tr>
                  <th>Sheet</th>
                  <th>Header row</th>
                  <th>Rows</th>
                  <th>With Student ID</th>
                  <th>Without Student ID</th>
                </tr>
              </thead>
              <tbody>
                {p.sheets.map((s) => (
                  <tr key={s.name}>
                    <td>{s.name}</td>
                    <td>{s.found ? s.headerRow || "—" : "sheet missing"}</td>
                    <td>{s.totalRows}</td>
                    <td>{s.rowsWithId ?? "—"}</td>
                    <td>{s.noIdRows ? s.noIdRows.length : 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="Column mapping">
          {p.sheets.map((s) => (
            <div key={s.name} className="spk-imp-sub">
              <p className="spk-imp-subtitle">{s.name}</p>
              <div className="adm-table-wrap">
                <table className="adm-table adm-table--compact">
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Header</th>
                      <th>Used as</th>
                      <th>TRUE</th>
                      <th>FALSE</th>
                      <th>Empty</th>
                      <th>Unsupported</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.columns.map((c) => {
                      const counts = c.itemKey ? s.itemCounts[c.itemKey] : null;
                      return (
                        <tr key={c.column}>
                          <td>{c.column}</td>
                          <td>{c.header || <span className="spk-dim">—</span>}</td>
                          <td>{c.role === "item" ? <b>{c.itemName}</b> : ROLE_LABEL[c.role]}</td>
                          <td>{counts ? counts.true : ""}</td>
                          <td>{counts ? counts.false : ""}</td>
                          <td>{counts ? counts.blank : ""}</td>
                          <td className={counts && counts.unsupported ? "text-danger fw-bold" : ""}>{counts ? counts.unsupported : ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          <p className="spk-dim" style={{ margin: "8px 0 0" }}>
            TRUE = received. FALSE or empty = not received (no active record). Items whose column is not in a sheet are not changed for students
            of that sheet.
          </p>
        </Section>

        <Section title="Students" open>
          <div className="spk-imp-stats">
            <Stat label="Unique Student IDs" value={p.students.uniqueInWorkbook} />
            <Stat label="Matched" value={p.students.matched} />
            <Stat label="Not found (skipped)" value={p.students.unmatchedTotal} tone={p.students.unmatchedTotal ? "warning" : ""} />
            <Stat label="In both sheets" value={p.students.inBoth.length} />
            <Stat label="Ambiguous IDs" value={p.students.ambiguous.length} tone={p.students.ambiguous.length ? "danger" : ""} />
            <Stat label="Duplicate IDs in a sheet" value={p.students.duplicates.length} />
          </div>
          {p.students.ambiguous.length > 0 && (
            <ul className="spk-imp-list is-error">
              {p.students.ambiguous.map((a) => (
                <li key={a.studentId}>
                  {a.studentId}: {a.records.map((r) => `#${r.studentRowId} ${r.name} (batch ${r.batch || "—"})`).join(", ")}
                </li>
              ))}
            </ul>
          )}
          {p.students.unmatchedTotal > 0 && (
            <details className="spk-imp-inner">
              <summary>Student IDs not found in the website ({p.students.unmatchedTotal})</summary>
              <ul className="spk-imp-list">
                {p.students.unmatched.map((u) => (
                  <li key={u.studentId}>
                    {u.studentId} — {u.name || "no name"} <span className="spk-dim">({u.sources.map((s) => `${s.sheet} row ${s.row}`).join(", ")})</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {p.students.inBoth.length > 0 && (
            <details className="spk-imp-inner">
              <summary>Student IDs in both sheets ({p.students.inBoth.length}) — combined</summary>
              <p className="spk-imp-meta">{p.students.inBoth.join(", ")}</p>
            </details>
          )}
          {p.sheets.map(
            (s) =>
              s.noIdRows &&
              s.noIdRows.length > 0 && (
                <details key={s.name} className="spk-imp-inner">
                  <summary>
                    {s.name}: rows without Student ID ({s.noIdRows.length}) — skipped
                  </summary>
                  <ul className="spk-imp-list">
                    {s.noIdRows.map((n) => (
                      <li key={n.row}>
                        Row {n.row}
                        {n.name ? ` — ${n.name}` : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              ),
          )}
        </Section>

        <Section title="Changes per item" open>
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--compact">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>New received</th>
                  <th>Pending → received</th>
                  <th>Kept</th>
                  <th>Removed</th>
                  <th>No record (stays)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(r.perItem).map(([key, v]) => (
                  <tr key={key}>
                    <td>
                      {v.itemName}
                      {r.itemsToCreate.some((i) => i.itemKey === key) && <span className="spk-tag">new item</span>}
                    </td>
                    <td>{v.create}</td>
                    <td>{v.update}</td>
                    <td>{v.preserve}</td>
                    <td className={v.remove ? "text-danger fw-bold" : ""}>{v.remove}</td>
                    <td>{v.none}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.removalsTotal > 0 && (
            <details className="spk-imp-inner">
              <summary>Records that will be removed ({r.removalsTotal})</summary>
              <ul className="spk-imp-list">
                {r.removals.map((x, i) => (
                  <li key={i}>
                    {x.studentId} {x.name} — {x.itemName} (currently {x.previousStatus}; workbook: {x.value.toUpperCase()} in {x.sheet} row {x.row})
                  </li>
                ))}
              </ul>
            </details>
          )}
          <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm mt-2" onClick={downloadPlan}>
            <i className="bi bi-download"></i> Download full plan (CSV)
          </button>
        </Section>

        {p.warnings.length > 0 && (
          <Section title="Notes" count={p.warnings.length}>
            <ul className="spk-imp-list">
              {p.warnings.map((w, i) => (
                <li key={i}>{w.message}</li>
              ))}
            </ul>
          </Section>
        )}
      </>
    );
  };

  const renderResult = () => (
    <div className={`spk-callout ${result.status === "completed" ? "is-info" : "is-error"}`} role="status">
      <i className={`bi ${result.status === "completed" ? "bi-check2-circle" : "bi-exclamation-triangle"}`} aria-hidden="true"></i>
      <div>
        <p className="spk-callout-title">{result.status === "completed" ? "Import completed and verified" : "Import saved, but verification found differences"}</p>
        <p className="spk-callout-text" style={{ margin: 0 }}>
          {result.created} new received · {result.updated} pending → received · {result.removed} removed · {result.preserved} kept ·{" "}
          {result.itemsCreated} new item(s) · {result.historyEntries} history entries.
          {result.status !== "completed" && ` ${result.remainingChanges} difference(s) remain — preview the workbook again.`}
        </p>
      </div>
    </div>
  );

  return createPortal(
    <div className="adm-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="adm-modal spk-detail" style={{ width: "880px" }} role="dialog" aria-modal="true" aria-labelledby="spk-imp-title" onClick={(e) => e.stopPropagation()}>
        <div className="adm-modal-header">
          <div>
            <h5 id="spk-imp-title" className="adm-card-title" style={{ fontSize: "17px" }}>
              Import from Excel
            </h5>
            <p className="adm-card-subtitle">
              Reads the “MASTER DATA” and “ANGKATAN #5” sheets. Students are matched by Student ID only; the workbook decides which items each listed
              student has received.
            </p>
          </div>
          <button type="button" className="adm-modal-close" aria-label="Close" onClick={onClose} disabled={busy}>
            ×
          </button>
        </div>

        <div className="adm-modal-body">
          <div className="spk-imp-pick">
            <label className="adm-btn adm-btn--secondary mb-0">
              <i className="bi bi-file-earmark-excel"></i> {file ? "Change workbook" : "Choose workbook (.xlsx)"}
              <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={choose} hidden disabled={busy} />
            </label>
            <span className="spk-imp-file">{file ? `${file.name} · ${(file.size / 1024).toFixed(0)} KB` : "No file chosen"}</span>
            <button type="button" className="adm-btn adm-btn--primary" onClick={runPreview} disabled={!file || busy}>
              {busy && !preview ? "Reading…" : "Preview"}
            </button>
          </div>

          {result && renderResult()}
          {preview && renderPreview()}

          {!preview && (
            <Section title="Recent imports" count={runs ? runs.length : undefined} open={!result}>
              {!runs ? (
                <p className="adm-muted-text">Loading…</p>
              ) : runs.length === 0 ? (
                <p className="adm-muted-text">No imports yet.</p>
              ) : (
                <ul className="spk-history">
                  {runs.map((run) => (
                    <li key={run.runId}>
                      <p className="spk-history-title">
                        <span className={`adm-status ${STATUS_CLASS[run.status] || ""}`}>{run.status}</span> {run.fileName}
                        {run.blocked && run.status === "previewed" ? " · blocked" : ""}
                      </p>
                      <p className="spk-history-meta">
                        Preview {formatDateTime(run.createdAt)} by {run.createdByName || "—"}
                        {run.finishedAt ? ` · finished ${formatDateTime(run.finishedAt)} by ${run.executedByName || "—"}` : ""}
                        {run.result ? ` · ${run.result.created} new, ${run.result.updated} updated, ${run.result.removed} removed` : ""}
                      </p>
                      {run.error && <p className="spk-history-reason">{run.error}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          )}
        </div>

        <div className="adm-modal-footer">
          <button type="button" className="adm-btn adm-btn--secondary" onClick={onClose} disabled={busy}>
            Close
          </button>
          {preview && (
            <button
              type="button"
              className="adm-btn adm-btn--primary"
              onClick={execute}
              disabled={busy || preview.blocked || preview.noChanges}
              title={preview.blocked ? "Fix the problems listed above first" : preview.noChanges ? "Nothing to import" : ""}
            >
              {busy ? "Importing…" : "Confirm import"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ImportModal;
